import React from 'react';
import { Router, Route, RootRoute, redirect } from '@tanstack/react-router';
import { jwtDecode } from 'jwt-decode';
import App from './App';
import LandingPage from './pages/info/LandingPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';

const MainInteractiveMap = React.lazy(() => import('./pages/map/MainInteractiveMap'));
const EddiesInteractiveMap = React.lazy(() => import('./pages/map/EddiesInteractiveMap'));
const WavePage = React.lazy(() => import('./pages/dashboards/WavePage'));
const DashboardEnsoPeru = React.lazy(() => import('./pages/dashboards/DashboardEnsoPeru'));
const DashboardHub = React.lazy(() => import('./pages/dashboards/DashboardHub'));
const DashboardEstadoMar = React.lazy(() => import('./pages/dashboards/DashboardEstadoMar'));
const ServiceSelectionPage = React.lazy(() => import('./pages/subscription/ServiceSelectionPage'));
const MaskPage = React.lazy(() => import('./pages/map/MaskPage'));
const ForecastInteractiveMap = React.lazy(() => import('./pages/map/ForecastInteractiveMap'));
const AdminPage = React.lazy(() => import('./pages/admin/AdminPage'));
const CalasWeeklyReportsPage = React.lazy(() => import('./pages/admin/CalasWeeklyReportsPage'));
const ModelAccessPage = React.lazy(() => import('./pages/admin/ModelAccessPage'));
const WindPage = React.lazy(() => import('./pages/map/WindPage'));
const FishingToolsPage = React.lazy(() => import('./pages/info/FishingToolsPage'));
const SeaStatePage = React.lazy(() => import('./pages/info/SeaStatePage'));
const ModelosPredictivosPage = React.lazy(() => import('./pages/map/ModelosPredictivosPage'));
const ZonasPescaIaHub = React.lazy(() => import('./pages/map/ZonasPescaIaHub'));
const ZonasPescaIaJurel = React.lazy(() => import('./pages/map/ZonasPescaIaJurel'));
const PredictiveModelsBySpecies = React.lazy(() => import('./pages/map/PredictiveModelsBySpecies'));
const TidesPage = React.lazy(() => import('./pages/dashboards/TidesPage'));
const CheckoutPage = React.lazy(() => import('./pages/subscription/CheckoutPage'));
const SubscriptionPage = React.lazy(() => import('./pages/subscription/SubscriptionPage'));
const ProfesionalPlanPage = React.lazy(() => import('./pages/subscription/ProfesionalPlanPage'));
const VerifyPaymentPage = React.lazy(() => import('./pages/subscription/VerifyPaymentPage'));
const GuiaPage = React.lazy(() => import('./pages/info/GuiaPage'));

// --- Authentication Checks ---
const checkAuth = (redirectPath) => () => {
  const token = localStorage.getItem('accessToken');
  console.log('[checkAuth] redirectPath:', redirectPath, 'hasToken:', !!token);
  if (!token) {
    console.log('[checkAuth] No token, redirecting to login');
    throw redirect({ to: '/login', search: { redirect: redirectPath } });
  }
  try {
    const decodedToken = jwtDecode(token);
    if (decodedToken.exp * 1000 < Date.now()) {
      console.log('[checkAuth] Token expired, redirecting to login');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('is_superuser');
      localStorage.removeItem('subscriptionPlan');
      throw redirect({ to: '/login', search: { redirect: redirectPath } });
    }
  } catch (error) {
    console.log('[checkAuth] Error validating token:', error);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('is_superuser');
    localStorage.removeItem('subscriptionPlan');
    throw redirect({ to: '/login', search: { redirect: redirectPath } });
  }
};

const checkAdminAuth = (redirectPath) => () => {
  // 1. Run the standard authentication check first
  checkAuth(redirectPath)();

  // 2. Check for superuser status
  const isSuperuser = localStorage.getItem('is_superuser') === 'true';
  if (!isSuperuser) {
    // If not an admin, redirect to a safe, non-admin page
    throw redirect({ to: '/select-service' });
  }
};

const PLAN_LEVEL = { 'BOD Intelligence': 1, 'BOD Forecast': 2, 'Personalizado': 3 };

const checkPlanAuth = (requiredPlan, redirectPath) => () => {
  checkAuth(redirectPath)();

  const isSuperuser = localStorage.getItem('is_superuser') === 'true';
  if (isSuperuser) return;

  const userPlan = localStorage.getItem('subscriptionPlan');
  const requiredLevel = PLAN_LEVEL[requiredPlan] || 0;
  const userLevel = PLAN_LEVEL[userPlan] || 0;

  if (userLevel < requiredLevel) {
    throw redirect({ to: '/select-service' });
  }
};


// --- Route Definitions ---

// Create a root route
const rootRoute = new RootRoute({ component: App });

// Create child routes
const indexRoute = new Route({ getParentRoute: () => rootRoute, path: '/', component: LandingPage });
const loginRoute = new Route({ 
  getParentRoute: () => rootRoute, 
  path: '/login', 
  component: LoginPage,
  validateSearch: (search) => ({
    token: search.token,
    error: search.error,
    plan: search.plan,
    is_superuser: search.is_superuser
  })
});
const registerRoute = new Route({ getParentRoute: () => rootRoute, path: '/register', component: RegisterPage });

// Protected Routes
const serviceSelectionRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/select-service',
  component: ServiceSelectionPage,
  beforeLoad: checkAuth('/select-service')
});

const fishingToolsRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/fishing-tools',
  component: FishingToolsPage,
  beforeLoad: checkAuth('/fishing-tools')
});

const seaStateRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/sea-state',
  component: SeaStatePage,
  beforeLoad: checkAuth('/sea-state')
});

const modelosPredictivosRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/modelos_predictivos',
  component: ModelosPredictivosPage,
  beforeLoad: checkAuth('/modelos_predictivos')
});

const zonasPescaIaHubRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/zonas_pesca_ia',
  component: ZonasPescaIaHub,
  beforeLoad: checkAuth('/zonas_pesca_ia')
});

const zonasPescaIaJurelRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/zonas_pesca_ia/jurel',
  component: ZonasPescaIaJurel,
  beforeLoad: checkAuth('/zonas_pesca_ia/jurel')
});

// Legacy redirects: old FishMap AI URLs
const legacyFishMapAiHubRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/fishmap-ai',
  beforeLoad: () => { throw redirect({ to: '/zonas_pesca_ia' }); }
});

const legacyFishMapAiJurelRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/fishmap-ai/jurel',
  beforeLoad: () => { throw redirect({ to: '/zonas_pesca_ia/jurel' }); }
});

const speciesPredictiveMapRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/modelos_predictivos/especie',
  component: PredictiveModelsBySpecies,
  beforeLoad: checkAuth('/modelos_predictivos/especie')
});



const maininteractivemap = new Route({
  getParentRoute: () => rootRoute,
  path: '/maininteractivemap',
  component: MainInteractiveMap,
  // No requerir auth - permitir acceso libre al visualizador
});

const eddiesRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/eddies',
  component: EddiesInteractiveMap,
  beforeLoad: checkAuth('/eddies')
});

const waveRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/dashboard_oleaje',
  component: WavePage,
  beforeLoad: checkAuth('/dashboard_oleaje')
});

const dashboardHubRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/dashboard_hub',
  component: DashboardHub,
});

const dashboardEnsoPeruRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/dashboard_enso_peru',
  component: DashboardEnsoPeru,
});

const dashboardEstadoMarRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/dashboard_estado_mar',
  component: DashboardEstadoMar,
});

const maskRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/mascara',
    component: MaskPage,
    beforeLoad: checkAuth('/mascara')
});

const forecastRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/forecast',
  component: ForecastInteractiveMap,
  beforeLoad: checkPlanAuth('BOD Forecast', '/forecast')
});

const windRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/vientos-olas',
  component: WindPage,
  beforeLoad: checkAuth('/vientos-olas')
});

const tidesRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/mareas',
  component: TidesPage,
  beforeLoad: checkAuth('/mareas')
});

const checkoutRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/checkout',
  component: CheckoutPage,
  beforeLoad: checkAuth('/checkout'),
  validateSearch: (search) => ({
    plan: search.plan,
    session_id: search.session_id,
    success: search.success,
    cancelled: search.cancelled
  })
});

const subscriptionRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/subscription',
  component: SubscriptionPage,
  beforeLoad: checkAuth('/subscription')
});

const subscriptionSuccessRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/subscription/success',
  component: CheckoutPage,
  beforeLoad: checkAuth('/subscription/success'),
  validateSearch: (search) => ({
    preference_id: search.preference_id,
    success: search.success
  })
});

const subscriptionCancelledRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/subscription/cancelled',
  component: CheckoutPage,
  beforeLoad: checkAuth('/subscription/cancelled'),
  validateSearch: (search) => ({
    reason: search.reason
  })
});

const profesionalPlanRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/plan/profesional',
  component: ProfesionalPlanPage
});

const verifyPaymentRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/verify-payment',
  component: VerifyPaymentPage,
  beforeLoad: checkAuth('/verify-payment')
});

const guiaRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/guia_usuario',
  component: GuiaPage
});

// Admin Routes
const adminRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: AdminPage,
  beforeLoad: checkAdminAuth('/admin')
});

const calasWeeklyReportsRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/admin/calas_weekly_reports',
  component: CalasWeeklyReportsPage,
  beforeLoad: checkAdminAuth('/admin/calas_weekly_reports')
});

const modelAccessRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/admin/model_access',
  component: ModelAccessPage,
  beforeLoad: checkAdminAuth('/admin/model_access')
});


// Create the route tree
const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  registerRoute,
  serviceSelectionRoute,
  maininteractivemap,
  eddiesRoute,
  waveRoute,
  dashboardHubRoute,
  dashboardEnsoPeruRoute,
  dashboardEstadoMarRoute,
  maskRoute,
  forecastRoute,
  windRoute,
  adminRoute,
  calasWeeklyReportsRoute,
  modelAccessRoute,
  fishingToolsRoute,
  seaStateRoute,
  modelosPredictivosRoute,
  zonasPescaIaHubRoute,
  zonasPescaIaJurelRoute,
  legacyFishMapAiHubRoute,
  legacyFishMapAiJurelRoute,
  speciesPredictiveMapRoute,
  tidesRoute,
  checkoutRoute,
  subscriptionRoute,
  subscriptionSuccessRoute,
  subscriptionCancelledRoute,
  profesionalPlanRoute,
  verifyPaymentRoute,
  guiaRoute,
]);

// Create the router
export const router = new Router({ routeTree });
